import { PropsWithChildren, useMemo, useReducer } from "react";

import WineClubs from "../../components/wine-clubs/clubs-definition";
import contextLogger from "../../utils/context-logger";
import WineClubsContext, { WineClubsContextInterface } from "./context";
import { defaultStateForClub, reducer, stateFromPlanName } from "./reducer";
import { initialWineClubsState } from "./state";
import {
  WineClub,
  WineClubFrequency,
  WineClubPreference,
  WineClubVariant,
} from "./types";

interface WineClubsProviderProps {
  club?: WineClub;
  plan?: string;
}

const WineClubsProvider = ({
  club,
  plan,
  children,
}: PropsWithChildren<WineClubsProviderProps>): JSX.Element => {
  let defaultState = initialWineClubsState;

  if (undefined !== plan) {
    defaultState = stateFromPlanName(plan);
  } else if (undefined !== club) {
    defaultState = defaultStateForClub(club);
  }

  const [state, dispatch] = useReducer(
    process.env.NODE_ENV === "development" ? contextLogger(reducer) : reducer,
    defaultState
  );

  const clubOptions: WineClub[] = WineClubs;

  const changeClub = (club: WineClub): void => {
    dispatch({ type: "CHANGE_CLUB", club });
  };

  const variantOptions: WineClubVariant[] = useMemo(() => {
    const { club } = state;
    return club ? club.variants : [];
  }, [state.club]);

  const changeVariant = (variant: WineClubVariant): void => {
    dispatch({ type: "CHANGE_VARIANT", variant });
  };

  const preferenceOptions: WineClubPreference[] = useMemo(() => {
    const { variant } = state;
    return variant ? variant.preferences : [];
  }, [state.variant]);

  const changePreference = (preference: WineClubPreference): void => {
    dispatch({ type: "CHANGE_PREFERENCE", preference });
  };

  const frequencyOptions: WineClubFrequency[] = useMemo(() => {
    const { club } = state;
    return club ? club.frequencies : [];
  }, [state.club]);

  const changeFrequency = (frequency: WineClubFrequency): void => {
    dispatch({ type: "CHANGE_FREQUENCY", frequency });
  };

  const planName = useMemo(() => {
    const { club, variant, preference, frequency } = state;
    return [club?.key, variant?.key, preference?.key, frequency?.key].join("|");
  }, [state.club, state.variant, state.preference, state.frequency]);

  const ctx: WineClubsContextInterface = {
    changeClub,
    clubOptions,
    changeVariant,
    variantOptions,
    preferenceOptions,
    changePreference,
    frequencyOptions,
    changeFrequency,
    planName,
    ...state,
  };

  return (
    <WineClubsContext.Provider value={{ ...ctx }}>
      {children}
    </WineClubsContext.Provider>
  );
};

export default WineClubsProvider;
